import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import isEqual from 'react-fast-compare';

import {Owner} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {SavingState} from 'constants/RegistryConstants';
import {EntitySummary} from 'constants/UserConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {DATA_TEST_ATTRS} from 'instruments/connection-schemas/ConnectionSchemaMetadataSection';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import FieldTable from 'registry/field/FieldTable';
import SchemaNameInput from 'schemas/SchemaNameInput';
import SchemaOwnerDisplay from 'schemas/SchemaOwnerDisplay';
import SchemaPrefixInput from 'schemas/SchemaPrefixInput';
import {getOwnerById} from 'schemas/SchemaUtil';
import SchemaWarehouseNameField from 'schemas/SchemaWarehouseNameField';
import StudySchemaFieldTableRow from 'studies/study-schemas/StudySchemaFieldTableRow';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {getEnabledStudyTypes, supportsManualIdGeneration} from 'studies/TypedStudyUtils';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {SILVER, GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import runModal from 'ui-library/runModal';
import {SettingsLabel} from 'ui-library/SettingsField';

import {StudySchemaQuery} from './__generated/StudySchemaQuery';
import {StudySchemaQuery_studySchema_StudySchemaNode_entryTemplates as EntryTemplateIconOrInaccessibleInfo} from './__generated/StudySchemaQuery';
import SetEntryTemplatesOnStudySchemasModal from './SetEntryTemplatesOnStudySchemasModal';
import StudyNameTemplateField from './StudyNameTemplateField';
import StudySchemaEntryTemplatesTable from './StudySchemaEntryTemplatesTable';
import StudySchemaGeneralSettingsEditor from './StudySchemaGeneralSettingsEditor';
import StudySchemaTypeDropdown from './StudySchemaTypeDropdown';

type StudySchema = ExtractByTypename<StudySchemaQuery['studySchema'], 'StudySchemaNode'>;

function Row({children}: {children: JSX.Element}): JSX.Element {
  return <div className={css(styles.schemaMetadataRow)}>{children}</div>;
}

function Col({children}: {children?: JSX.Element}): JSX.Element {
  return <div className={css(styles.schemaMetadataColumn)}>{children}</div>;
}

function FieldWrapper({children}: {children: JSX.Element}): JSX.Element {
  return <div className={css(styles.schemaMetadataField)}>{children}</div>;
}

export interface StudySchemaMetadataSettingsProps {
  ownerId: string;
  setOwnerId: (id: string) => void;
  name: string;
  setName: (id: string) => void;
  nameTemplateParts: Array<NameTemplatePart>;
  setNameTemplateParts: (parts: Array<NameTemplatePart>) => Promise<boolean>;
  prefix: string;
  setPrefix: (id: string) => void;
  fields: Array<ClientSchemaField>;
  studyType: StudyType;
  setStudyType: (studyType: StudyType) => void;
  setFields: (fields: Array<ClientSchemaField>) => void;
  isDesignApprovalRequired: boolean;
  setIsDesignApprovalRequired: (isDesignApprovalRequired: boolean) => void;
  handleNext?: () => void;
  ownerChoices: Array<Owner>;
  isBeingCreated: boolean;
  schemaId?: string;
  schema?: StudySchema;
  entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>;
  setEntryTemplates: (entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>) => void;
  autoGenerateId: boolean;
  setAutoGenerateId: (autoGenerateId: boolean) => void;
}

// Keep in sync with benchling.studies.domain.constants.py
export const AUTO_GENERATE_ID_LABEL = 'Autogenerate ID with a specified prefix';
export const MANUAL_ID_LABEL = 'Manually specify ID at creation time';
export const NAMING_TEMPLATE_TOOLTIP_MESSAGE =
  'You can automatically name studies based on a naming template once this schema has been created. If the name template is changed, it will not affect the names of existing studies.';
export const SET_NAMING_TEMPLATE_AFTER_SCHEMA_CREATION_MESSAGE = 'Set after creating Study schema';
/**
 * Presentational component for configuring a StudySchema, including both system fields and schema fields.
 */
export default function StudySchemaMetadataSettings({
  ownerId,
  setOwnerId,
  name,
  setName,
  nameTemplateParts,
  setNameTemplateParts,
  prefix,
  setPrefix,
  fields,
  setFields,
  isDesignApprovalRequired,
  setIsDesignApprovalRequired,
  studyType,
  setStudyType,
  handleNext,
  ownerChoices,
  isBeingCreated,
  schemaId,
  schema,
  entryTemplates,
  setEntryTemplates,
  autoGenerateId,
  setAutoGenerateId,
}: StudySchemaMetadataSettingsProps): JSX.Element {
  const handleOnAddClick: () => Promise<void> = async () => {
    await runModal(SetEntryTemplatesOnStudySchemasModal, {
      setEntryTemplates,
      selectedTemplates: entryTemplates,
    });
  };

  let error = null;
  if (!name) {
    error = 'Schema needs a name';
  } else if (!prefix && autoGenerateId) {
    error = 'Schema needs a prefix';
  } else if (fields?.some((f) => !f.name)) {
    error = 'Schema fields must all have names';
  }

  const nameTemplatesHelpIcon = (
    <div>
      <Tooltip text={NAMING_TEMPLATE_TOOLTIP_MESSAGE}>
        <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
      </Tooltip>
    </div>
  );

  return (
    // TODO(BNCH-64599) extract styles into shared Schemas components
    <div className="u-flex-grow u-min-width-zero">
      <div className="settingsPageContainer-section">
        <Row>
          <>
            <Col>
              <FieldWrapper>
                <SchemaNameInput
                  isWritable={true}
                  schema={schema}
                  onNameChange={setName}
                  name={name}
                  dataTestAttrs={{component: 'StudySchemaEditor', element: 'nameInput'}}
                />
              </FieldWrapper>
            </Col>
            {!isEqual(getEnabledStudyTypes(), [StudyType.CUSTOM]) && (
              <Col>
                <StudySchemaTypeDropdown
                  value={studyType}
                  onChange={setStudyType}
                  isBeingCreated={isBeingCreated}
                />
              </Col>
            )}
            <Col>
              <FieldWrapper>
                <SchemaOwnerDisplay
                  ownerId={ownerId}
                  ownerChoices={ownerChoices}
                  onOwnerChange={(owner: EntitySummary) => {
                    setOwnerId(owner.id);
                  }}
                  schema={schema}
                  dataTestAttrs={DATA_TEST_ATTRS.owner}
                />
              </FieldWrapper>
            </Col>
          </>
        </Row>
        <Row>
          <>
            {supportsManualIdGeneration(studyType) && (
              <Col>
                <RadioButtonGroup<boolean>
                  title="ID"
                  options={[
                    {label: AUTO_GENERATE_ID_LABEL, value: true},
                    {label: MANUAL_ID_LABEL, value: false},
                  ]}
                  onChange={setAutoGenerateId}
                  selectedValue={autoGenerateId}
                  isVertical={true}
                  disabled={!isBeingCreated}
                />
              </Col>
            )}
            <Col>
              <FieldWrapper>
                <SchemaPrefixInput
                  key={String(!isBeingCreated && autoGenerateId && prefix === '')}
                  isBeingCreated={isBeingCreated}
                  isRequired={autoGenerateId}
                  isWritable={autoGenerateId}
                  onPrefixChange={setPrefix}
                  prefix={prefix}
                  dataTestAttrs={{component: 'StudySchemaEditor', element: 'prefixInput'}}
                />
              </FieldWrapper>
            </Col>
            {BenchlingConfig.STUDIES_ENABLE_NAMING_TEMPLATES && (
              <Col>
                {!isBeingCreated ? (
                  <FieldWrapper>
                    <StudyNameTemplateField
                      nameTemplateParts={nameTemplateParts}
                      schemaFields={fields}
                      schemaId={schemaId ?? null}
                      isWritable={true}
                      setNameTemplateParts={setNameTemplateParts}
                    />
                  </FieldWrapper>
                ) : (
                  <>
                    <SettingsLabel
                      helpIcon={nameTemplatesHelpIcon}
                      isRequired={false}
                      label="Name template"
                    />
                    <div className={css(styles.nameTemplateMessageText)}>
                      {SET_NAMING_TEMPLATE_AFTER_SCHEMA_CREATION_MESSAGE}
                    </div>
                  </>
                )}
              </Col>
            )}
          </>
        </Row>
        {!isBeingCreated && schema != null && (
          <Row>
            <Col>
              <SchemaWarehouseNameField
                onChange={() => {}}
                isWritable={false}
                sqlIdentifier={schema.sqlIdentifier}
              />
            </Col>
          </Row>
        )}
      </div>
      <div className={css(styles.settingsGroup)}>
        {/* TODO(BNCH-64047) - support entry and bioentity links properly */}
        {/* TODO(BNCH-64042) - fill in isDisabled properly */}
        <FieldTable
          schemaId={schemaId}
          isBeingCreated={isBeingCreated}
          schemaHasData={false}
          isDisabled={false}
          itemClass={StudySchemaFieldTableRow}
          fields={fields}
          onFieldsChange={setFields}
          schemaType="study"
          tagSchemasFilter={() => false}
          batchSchemas={[]}
          storableSchemas={[]}
          assaySchemas={[]}
          ownerUrlId={getOwnerById(ownerId)?.url_identifier || ''}
          ownerId={ownerId}
          shouldShowIsRequired={true}
          shouldShowIsIntegration={false}
          shouldShowIsParentLink={false}
          dataTestAttrs={{component: 'StudySchemaCreateFlow', element: 'fieldTable'}}
          savingState={SavingState.COMPLETED}
        />
      </div>
      {studyType === StudyType.CUSTOM && (
        <StudySchemaEntryTemplatesTable
          entryTemplates={entryTemplates}
          onAddClick={handleOnAddClick}
          writable={true}
        />
      )}
      {getTypedStudyAdapter(studyType).allowRequireDesignApproval && (
        <div className={css(styles.settingsGroup)}>
          <StudySchemaGeneralSettingsEditor
            isDesignApprovalRequired={isDesignApprovalRequired}
            setIsDesignApprovalRequired={setIsDesignApprovalRequired}
          />
        </div>
      )}
      {handleNext ? (
        <TooltipWrapper tooltipTitle={error}>
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={handleNext}
            size={ButtonSize.MEDIUM}
            text="Next"
            disabled={error != null}
            dataTestAttrs={{component: 'StudySchemaMetadataSettings', element: 'nextButton'}}
          />
        </TooltipWrapper>
      ) : null}
    </div>
  );
}

const styles = StyleSheet.create({
  settingsGroup: {
    marginTop: '32px',
    marginBottom: '32px',
  },
  schemaMetadataRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: '32px',
  },
  schemaMetadataColumn: {
    flex: '0 0 30%',
    minWidth: 0,
    ':after': {
      flex: '1 1 auto',
    },
    ':nth-child(2)': {
      margin: '0 5% 0 5%',
    },
  },
  schemaMetadataField: {
    width: 'auto',
    marginBottom: '0',
  },
  infoIcon: {
    color: SILVER,
    paddingLeft: 4,
  },
  nameTemplateMessageText: {
    color: GRAY_DARKER,
    paddingTop: 3,
  },
});
